"use client";

import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import { getBuyNowDestinationPath } from "@/app/directories/shop/lib/storefrontLogicHelper";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import {
  getOrganizationAssetsForOrg,
  getProductCategoryTranslations,
} from "@frontend/lib/actions/config-actions";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { cn } from "@frontend/shared/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { useKeenSlider } from "keen-slider/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { FeaturedCarouselSlide } from "./FeaturedCarouselSlide";
import { FeaturedCarouselSlideKiosk } from "./FeaturedCarouselSlideKiosk";
import { ProductOptionsContainer } from "./ProductOptionsContainer";

const dotVariants = cva(
  "w-4 h-1 rounded-full mx-1 p-1 cursor-pointer focus:outline-none",
  {
    variants: {
      active: {
        true: "bg-black w-8",
        false: "bg-gray-300",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

const navigationButtonVariants = cva(
  "w-12 h-12 fill-white cursor-pointer rounded-full flex items-center justify-center",
  {
    variants: {
      hasSecondaryBackground: {
        true: "", // No border when secondary background color exists
        false: "bg-white border border-black", // White bg with black border when no secondary color
      },
      kioskMode: {
        true: "flex-shrink-0", // Flex item in kiosk mode (not absolute)
        false: "absolute top-1/2 -translate-y-1/2 lg:block sm:hidden", // Absolute positioned for non-kiosk
      },
    },
    defaultVariants: {
      hasSecondaryBackground: false,
      kioskMode: false,
    },
  },
);

export const FeaturedCarousel = ({
  products,
  clientSlug,
  scrollId,
  orgId,
  isMinimal = false,
  primaryActionText,
  showIcon = false,
  iconURL,
  categoryDisplayName,
  kioskMode = false,
}: {
  products: GetProductsByCategoryIdQuery["findManyProducts"];
  clientSlug: string;
  scrollId: string;
  orgId: number;
  isMinimal?: boolean;
  primaryActionText: string;
  showIcon?: boolean;
  iconURL?: string;
  categoryDisplayName?: string;
  kioskMode?: boolean;
}) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [loaded, setLoaded] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const router = useRouter();
  const { send: sendKioskEvent } = useKioskEvent();

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const { data: configFlags, isPending: isConfigFlagsPending } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: productCategoryTranslations } = useQuery({
    queryKey: [
      "productCategoryTranslations",
      orgId,
      products.flatMap((p) => p.categoryIds),
    ],
    queryFn: () =>
      getProductCategoryTranslations(
        orgId,
        products.flatMap((p) => p.categoryIds),
      ),
    enabled: !!products.length,
  });

  const secondaryBackgroundColor = organizationAssets?.secondaryBackgroundColor;

  const hasCustomSecondaryColor =
    secondaryBackgroundColor && secondaryBackgroundColor !== "#ffffff";

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    slides: {
      spacing: 10,
      origin: "center",
    },
    breakpoints: {
      "(min-width: 320px)": {
        slides: {
          perView: 1.1,
          spacing: 10,
          origin: "center",
        },
      },
      "(min-width: 768px)": {
        slides: {
          perView: 1.0,
          spacing: 10,
          origin: "center",
        },
      },
    },
    created() {
      setLoaded(true);
    },
  });

  const selectedProduct = selectedProductId
    ? (products.find((p) => p.id === selectedProductId) ?? products[0])
    : products[0]; // Default to first product to avoid null

  const handleBuyNowClick = (
    product: GetProductsByCategoryIdQuery["findManyProducts"][number],
  ) => {
    const hrt = product.productToHrt[0].hrt;
    const hrtId = hrt.hrtId;

    if (kioskMode) {
      sendKioskEvent({
        type: "SUBMIT",
        data: { hrtIds: [hrtId] },
      });
      return;
    }

    if (product.productToHrt.length > 1) {
      setSelectedProductId(product.id);
      return;
    }

    // configFlags drives the schedule-vs-review routing decision; bail until
    // the query settles so an eager click can't silently land on /review.
    if (isConfigFlagsPending) return;
    router.push(
      getBuyNowDestinationPath({
        hrt,
        orgId,
        configFlags,
        clientSlug,
        hrtId,
      }),
    );
  };

  return (
    <div
      className={cn(
        "relative",
        kioskMode
          ? "flex items-center justify-center gap-4" // Flex row for kiosk: [button] [content] [button]
          : "flex justify-center", // Original for non-kiosk
      )}
    >
      {/* Render the left arrow outside in kiosk mode - now a flex item */}
      {kioskMode && currentSlide !== 0 && (
        <button
          type="button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            instanceRef.current?.prev();
          }}
          disabled={currentSlide === 0}
          className={cn(
            navigationButtonVariants({
              hasSecondaryBackground: !!hasCustomSecondaryColor,
              kioskMode: true,
            }),
          )}
          style={{
            backgroundColor: hasCustomSecondaryColor
              ? secondaryBackgroundColor
              : undefined,
          }}
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      {/* Invisible placeholder to keep content centered when left button is hidden */}
      {kioskMode && currentSlide === 0 && (
        <div className="w-12 flex-shrink-0" />
      )}

      <div
        className={cn(
          "relative lg:min-h-[80vh] sm:h-fit lg:px-[96px] lg:py-[96px] lg:pt-[108px] px-0 sm:py-8",
          kioskMode ? "lg:py-8 lg:px-0 sm:px-0 lg:h-fit w-full" : "w-full",
        )}
        id={scrollId}
      >
        {(showIcon || categoryDisplayName) && (
          <div
            className={cn(
              "flex flex-row items-center gap-4 mb-6 lg:pl-24 md:pl-0 pl-6",
              kioskMode && "mb-6 lg:pl-0 sm:pl-0",
            )}
          >
            {showIcon && iconURL && (
              <Image
                src={iconURL}
                alt={categoryDisplayName || "Category"}
                width={500}
                height={500}
                className={cn(
                  "w-12 h-12 object-contain",
                  kioskMode && "w-10 h-10",
                )}
              />
            )}
            {categoryDisplayName && (
              <h2
                className={cn("config-h4", kioskMode && "kiosk-h1 !text-3xl")}
              >
                {categoryDisplayName}
              </h2>
            )}
          </div>
        )}
        <div ref={sliderRef} className="keen-slider min-h-full relative">
          {products.map((product, idx) => {
            return kioskMode ? (
              <FeaturedCarouselSlideKiosk
                key={`carousel-slide-${product.id}}`}
                product={product}
                clientSlug={clientSlug}
                isMinimal={isMinimal}
                orgId={orgId}
                primaryActionText={primaryActionText}
                kioskMode={kioskMode}
                onBuyNowClick={() => handleBuyNowClick(product)}
              />
            ) : (
              <FeaturedCarouselSlide
                key={`carousel-slide-${product.id}}`}
                product={product}
                clientSlug={clientSlug}
                isMinimal={isMinimal}
                orgId={orgId}
                primaryActionText={primaryActionText}
                onBuyNowClick={() => handleBuyNowClick(product)}
              />
            );
          })}

          {loaded && instanceRef.current && !kioskMode && (
            <motion.div
              className="flex justify-start mt-0 absolute lg:bottom-4 sm:bottom-0 lg:left-[96px] sm:left-[18px]"
              layoutId="carousel-dots"
            >
              {[
                ...Array(
                  instanceRef.current.track.details?.slides?.length,
                ).keys(),
              ].map((idx) => {
                return (
                  <motion.button
                    type="button"
                    key={idx}
                    onClick={() => {
                      instanceRef.current?.moveToIdx(idx);
                    }}
                    className={cn(
                      dotVariants({ active: currentSlide === idx }),
                    )}
                    layoutId={`dot-${idx}`}
                  />
                );
              })}
            </motion.div>
          )}
        </div>

        {/* Render the left and right arrows inside in non-kiosk mode */}
        {!kioskMode && loaded && instanceRef.current && (
          <>
            {currentSlide !== 0 && (
              <button
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  instanceRef.current?.prev();
                }}
                disabled={currentSlide === 0}
                className={cn(
                  navigationButtonVariants({
                    hasSecondaryBackground: !!hasCustomSecondaryColor,
                    kioskMode: false,
                  }),
                  "left-20",
                )}
                style={{
                  backgroundColor: hasCustomSecondaryColor
                    ? secondaryBackgroundColor
                    : undefined,
                }}
              >
                <ChevronLeft className="ml-[10px] w-7 h-7" />
              </button>
            )}

            {currentSlide !==
              (instanceRef.current.track.details?.slides?.length ?? 1) - 1 && (
              <button
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  instanceRef.current?.next();
                }}
                className={cn(
                  navigationButtonVariants({
                    hasSecondaryBackground: !!hasCustomSecondaryColor,
                    kioskMode: false,
                  }),
                  "right-20",
                )}
                style={{
                  backgroundColor: hasCustomSecondaryColor
                    ? secondaryBackgroundColor
                    : undefined,
                }}
              >
                <ChevronRight className="ml-[10px] w-7 h-7" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Render the right arrow outside in kiosk mode - now a flex item */}
      {kioskMode &&
        currentSlide !==
          (instanceRef.current?.track.details?.slides?.length ?? 1) - 1 && (
          <button
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              instanceRef.current?.next();
            }}
            className={cn(
              navigationButtonVariants({
                hasSecondaryBackground: !!hasCustomSecondaryColor,
                kioskMode: true,
              }),
            )}
            style={{
              backgroundColor: hasCustomSecondaryColor
                ? secondaryBackgroundColor
                : undefined,
            }}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        )}

      {/* Invisible placeholder to keep content centered when right button is hidden */}
      {kioskMode &&
        currentSlide ===
          (instanceRef.current?.track.details?.slides?.length ?? 1) - 1 && (
          <div className="w-12 flex-shrink-0" />
        )}
      <ProductOptionsContainer
        product={selectedProduct}
        clientSlug={clientSlug}
        orgId={orgId}
        kioskMode={kioskMode}
        open={selectedProductId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedProductId(null);
          }
        }}
      />
    </div>
  );
};
